import React from 'react';
import { Language } from '../../pages/DevPortal';
import { Server, Activity, CheckCircle2, ArrowRightLeft } from 'lucide-react';

export default function WorkflowSection({ lang }: { lang: Language }) {
    const isEn = lang === 'en';

    return (
        <div className="animate-fade-in">
            <h1 className="text-3xl font-bold text-slate-900 mb-4">
                {isEn ? 'Standard Integration Workflow' : 'Flujo Estándar de Integración'}
            </h1>
            <p className="text-lg text-slate-600 mb-10 leading-relaxed">
                {isEn 
                    ? 'Hospital Status is designed to work in tandem with your existing Hospital Information System (HIS) or Electronic Health Record (EHR). Below is the recommended bidirectional sync workflow.'
                    : 'Hospital Status está diseñado para trabajar en conjunto con el Sistema de Información Hospitalaria (HIS) o Expediente Clínico (EHR). A continuación, el flujo recomendado de sincronización bidireccional.'}
            </p>

            <div className="relative border-l border-slate-200 ml-4 space-y-12 pb-10">
                
                {/* Step 1 */}
                <div className="relative pl-10">
                    <div className="absolute -left-5 top-1.5 w-10 h-10 bg-indigo-50 border-2 border-indigo-200 rounded-full flex items-center justify-center shadow-sm">
                        <Server className="w-5 h-5 text-indigo-600" />
                    </div>
                    
                    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                        <div className="flex items-center gap-3 mb-4 border-b border-slate-100 pb-4">
                            <span className="bg-slate-100 text-slate-700 font-bold text-xs uppercase px-2.5 py-1 rounded tracking-wider">Paso 1</span>
                            <h3 className="font-bold text-lg text-slate-900">
                                {isEn ? 'Patient Admission (HIS → Hospital Status)' : 'Ingreso del Paciente (HIS → Hospital Status)'}
                            </h3>
                        </div>
                        <p className="text-slate-600 text-sm mb-4 leading-relaxed">
                            {isEn 
                                ? 'When a patient arrives at reception and is scheduled for surgery, your HIS triggers a POST request to our API. This automatically registers the patient in the waiting room board.'
                                : 'Cuando un paciente llega a recepción y es admitido para cirugía, el HIS del hospital envía una petición POST a nuestra API. Esto registra automáticamente al paciente en la sala de espera.'}
                        </p>
                        <div className="bg-slate-900 rounded-lg p-4 font-mono text-xs text-indigo-300">
                            POST /api/patients<br/>
                            <span className="text-slate-400">{`{
  "hospitalId": "hosp_123",
  "name": "Maria Garcia",
  "procedureName": "Aphendicectomy",
  "doctorName": "Dr. Smith",
  "room": "OR-1"
}`}</span>
                        </div>
                    </div>
                </div>

                {/* Step 2 */}
                <div className="relative pl-10">
                    <div className="absolute -left-5 top-1.5 w-10 h-10 bg-emerald-50 border-2 border-emerald-200 rounded-full flex items-center justify-center shadow-sm">
                        <Activity className="w-5 h-5 text-emerald-600" />
                    </div>
                    
                    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                        <div className="flex items-center gap-3 mb-4 border-b border-slate-100 pb-4">
                            <span className="bg-slate-100 text-slate-700 font-bold text-xs uppercase px-2.5 py-1 rounded tracking-wider">Paso 2</span>
                            <h3 className="font-bold text-lg text-slate-900">
                                {isEn ? 'Surgical Progression (In-App)' : 'Progresión Quirúrgica (App Interna)'}
                            </h3>
                        </div>
                        <p className="text-slate-600 text-sm leading-relaxed">
                            {isEn 
                                ? 'Inside the Operating Room, the circulating nurse or anesthesiologist uses the Hospital Status Staff Dashboard on a tablet to verify patient details and tap to update milestones (Incision, 50%, Closure, Recovery). These updates stream in sub-second real-time to the waiting room TVs.'
                                : 'Dentro del quirófano, el personal médico utiliza el panel operativo de Hospital Status en una tablet. Verifican los datos iniciales y tocan para actualizar los hitos (Incisión, 50%, Cierre). Estos eventos se transmiten en tiempo real sub-segundo a las pantallas de la sala de espera.'}
                        </p>
                    </div>
                </div>

                {/* Step 3 */}
                <div className="relative pl-10">
                    <div className="absolute -left-5 top-1.5 w-10 h-10 bg-blue-50 border-2 border-blue-200 rounded-full flex items-center justify-center shadow-sm">
                        <ArrowRightLeft className="w-5 h-5 text-blue-600" />
                    </div>
                    
                    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                        <div className="flex items-center gap-3 mb-4 border-b border-slate-100 pb-4">
                            <span className="bg-slate-100 text-slate-700 font-bold text-xs uppercase px-2.5 py-1 rounded tracking-wider">Paso 3</span>
                            <h3 className="font-bold text-lg text-slate-900">
                                {isEn ? 'Closure & Data Export (Hospital Status → HIS/EHR)' : 'Cierre y Retorno de Datos (Hospital Status → HIS/EHR)'}
                            </h3>
                        </div>
                        <p className="text-slate-600 text-sm mb-4 leading-relaxed">
                            {isEn 
                                ? 'Once the patient exits the recovery room, Hospital Status can fire a secure Webhook back to your HIS/EHR, containing a complete JSON audit log of exact timestamps for every phase, facilitating billing and clinical record keeping.'
                                : 'Una vez que el paciente sale de recuperación y se completa el ciclo, Hospital Status puede disparar un Webhook seguro hacia el HIS/EHR del hospital. Este payload JSON contiene el registro inmutable de tiempos exactos para cada fase (ingreso, corte, cierre), facilitando facturación y el expediente clínico.'}
                        </p>
                        <div className="bg-slate-900 rounded-lg p-4 font-mono text-xs text-blue-300">
                            WEBHOOK PAYLOAD<br/>
                            <span className="text-slate-400">{`{
  "patientId": "pt_892",
  "status": "EXIT",
  "timeline": {
    "admissionTime": "2026-03-16T10:00:00Z",
    "surgeryStartTime": "2026-03-16T11:15:00Z",
    "recoveryStartTime": "2026-03-16T13:45:00Z",
    "exitTime": "2026-03-16T15:30:00Z"
  },
  "totalDurationMinutes": 330
}`}</span>
                        </div>
                    </div>
                </div>

            </div>

            <div className="mt-16 pt-10 border-t border-slate-200">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                    <span className="bg-emerald-100 text-emerald-700 p-2 rounded-lg">
                        <Activity className="w-5 h-5" />
                    </span>
                    {isEn ? 'Integration Quick Start (3 Simple Steps)' : 'Quick Start de Integración (3 Pasos Simples)'}
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                    <div className="bg-white border text-center border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                        <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">1</div>
                        <h3 className="font-bold text-slate-900 mb-2">{isEn ? 'Configure Middleware' : 'Configura tu Middleware'}</h3>
                        <p className="text-sm text-slate-600">
                            {isEn 
                                ? 'Use your hospital\'s existing engine (Tasy, Iguana, Mirth) to listen for local HL7 ADT (Admissions) messages.' 
                                : 'Usa el motor de tu hospital (Tasy, Iguana, Mirth) para escuchar los mensajes locales HL7 ADT (Admisiones).'}
                        </p>
                    </div>
                    
                    <div className="bg-white border text-center border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                        <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">2</div>
                        <h3 className="font-bold text-slate-900 mb-2">{isEn ? 'Transform Payload' : 'Transforma el Payload'}</h3>
                        <p className="text-sm text-slate-600">
                            {isEn 
                                ? 'Map the HL7 data to a simple JSON object matching our `POST /api/patients` schema.' 
                                : 'Mapea la data HL7 a un objeto JSON simple que coincida con nuestro esquema `POST /api/patients`.'}
                        </p>
                    </div>

                    <div className="bg-white border text-center border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                        <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">3</div>
                        <h3 className="font-bold text-slate-900 mb-2">{isEn ? 'Send the POST' : 'Envía el POST'}</h3>
                        <p className="text-sm text-slate-600">
                            {isEn 
                                ? 'Your Middleware makes a secure HTTP POST to Hospital Status using your hospital\'s Bearer Token. Done!' 
                                : 'Tu Middleware hace un HTTP POST seguro a Hospital Status usando tu Token Bearer. ¡Listo!'}
                        </p>
                    </div>
                </div>
            </div>

            <div className="mt-10 pt-10 border-t border-slate-200">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">
                    {isEn ? 'Standalone Mode (No Integration Required)' : 'Modo Independiente (Sin Integración)'}
                </h2>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                    <p className="text-slate-600 leading-relaxed mb-4">
                        {isEn 
                            ? 'Hospital Status is fully functional out-of-the-box without any API integration. If a hospital is not ready to integrate their HIS, the medical staff (Nurses or Doctors) can manually register patients directly through the Staff Dashboard in under 10 seconds.'
                            : 'Hospital Status es 100% funcional desde el día uno sin necesidad de integración por API. Si el hospital aún no está listo para conectar su sistema, el personal médico (enfermeros o doctores) puede registrar a los pacientes manualmente desde su panel en menos de 10 segundos.'}
                    </p>
                    <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4 ml-2">
                        <li>{isEn ? 'Staff opens the tablet in the pre-op area.' : 'El personal abre la tablet en el área pre-operatoria.'}</li>
                        <li>{isEn ? 'Taps "Add Patient" and enters Name, Doctor, and Procedure.' : 'Toca "Agregar Paciente" e ingresa el Nombre, Doctor y Procedimiento.'}</li>
                        <li>{isEn ? 'Patient instantly appears on the waiting room board.' : 'El paciente aparece instantáneamente en la pantalla de la sala de espera.'}</li>
                    </ul>
                    <p className="text-sm font-bold text-indigo-700 bg-indigo-50 px-4 py-2 rounded-lg inline-block">
                        {isEn 
                            ? 'The API integration is an optional enhancement for automation, not a requirement for usage.'
                            : 'La integración por API es una mejora opcional para automatizar, no un requisito para usar el producto.'}
                    </p>
                </div>
            </div>
        </div>
    );
}
